import { BACKEND_URL } from './config';

const HITS_PER_PAGE = 20;

/**
 * Build Strapi v3 query string from filters, search, sort, and pagination.
 * Strapi v3 uses _limit, _start, _sort, and field-based filtering.
 */
const buildStrapiQuery = ({ searchTerm, filters, sortBy, offset, location }, state) => {
    const params = new URLSearchParams();

    // Auto-detect Strapi v5 vs v3
    const isV5 = BACKEND_URL.includes('1338') || process.env.REACT_APP_IS_STRAPI_V5 === 'true';

    // Ensure relations like photos and users are populated for v5
    if (isV5) {
        params.set('populate', '*');
        
        // V5 Pagination
        params.set('pagination[limit]', HITS_PER_PAGE.toString());
        params.set('pagination[start]', (offset || 0).toString());

        // V5 Sorting
        const sortMapV5 = {
            'trending': 'trendingScore:desc',
            'products': 'createdAt:desc',
            'products_by_price_asc': 'price:asc',
            'products_by_price_desc': 'price:desc',
            'products_by_likes_desc': 'createdAt:desc', // Fallback, Strapi v5 doesn't easily sort by relation count out of box
            'products_newest': 'createdAt:desc',
            'products_oldest': 'createdAt:asc',
        };
        params.set('sort', sortMapV5[sortBy] || 'createdAt:desc');
    } else {
        // V3 Pagination
        params.set('_limit', HITS_PER_PAGE.toString());
        params.set('_start', (offset || 0).toString());

        // V3 Sorting
        const sortMapV3 = {
            'trending': 'trendingScore:DESC',
            'products': 'created_at:DESC',
            'products_by_price_asc': 'price:ASC',
            'products_by_price_desc': 'price:DESC',
            'products_by_likes_desc': 'likes:DESC',
            'products_newest': 'created_at:DESC',
            'products_oldest': 'created_at:ASC',
        };
        params.set('_sort', sortMapV3[sortBy] || 'created_at:DESC');
    }

    // Determine base filters based on the current route
    const pathname = window.location.pathname;

    if (pathname.includes('/merchant/')) {
        const merchantId = pathname.split('/merchant/')[1];
        if (isV5) {
            params.set('filters[users_permissions_user][id][$eq]', merchantId);
            params.set('filters[activeListing][$eq]', 'true');
            params.set('filters[stock][$ne]', '0');
        } else {
            params.set('users_permissions_user.id', merchantId);
            params.set('activeListing', 'true');
            params.set('stock_ne', '0');
        }
    } else if (pathname.includes('/listing') || pathname.includes('/dashboard/listings')) {
        // User's own listings — filter by current user
        if (state?.auth?.user?.id) {
            if (isV5) {
                params.set('filters[users_permissions_user][id][$eq]', state.auth.user.id);
            } else {
                params.set('users_permissions_user', state.auth.user.id);
            }
        }
    } else if (pathname.includes('/favorites')) {
        // Favorites — filter by liked_by user
        if (state?.auth?.user?.id) {
            if (isV5) {
                params.set('filters[being_liked_by][id][$eq]', state.auth.user.id);
                params.set('filters[activeListing][$eq]', 'true');
                params.set('filters[stock][$ne]', '0');
            } else {
                params.set('being_liked_by.id', state.auth.user.id);
                params.set('activeListing', 'true');
                params.set('stock_ne', '0');
            }
        }
    } else {
        // Browse page — only active, in-stock products
        if (isV5) {
            params.set('filters[activeListing][$eq]', 'true');
            params.set('filters[stock][$ne]', '0');
        } else {
            params.set('activeListing', 'true');
            params.set('stock_ne', '0');
        }
    }

    // Search term
    if (searchTerm && searchTerm.trim()) {
        if (isV5) {
            params.set('filters[name][$containsi]', searchTerm.trim());
        } else {
            params.set('_q', searchTerm.trim());
        }
    }

    // Apply field filters
    if (filters) {
        if (isV5) {
            if (filters.productType) params.set('filters[productType][$eq]', filters.productType);
            if (filters.forSell === true) params.set('filters[forSell][$eq]', 'true');
            if (filters.forRent === true) params.set('filters[forRent][$eq]', 'true');
            if (filters.isVirtual === true) params.set('filters[isVirtual][$eq]', 'true');
            else if (filters.isVirtual === false) params.set('filters[isVirtual][$eq]', 'false');
            if (filters.openToShipping === true) params.set('filters[openToShipping][$eq]', 'true');
            if (filters.localPickup?.enabled) {
                params.set('filters[openToPickUp][$eq]', 'true');
                if (location && filters.localPickup.distance) {
                    params.set('lat', location.latitude);
                    params.set('lng', location.longitude);
                    params.set('radius', filters.localPickup.distance);
                }
            }
            if (filters.category) params.set('filters[category][$eq]', filters.category);
            if (filters.size) params.set('filters[size][$eq]', filters.size);
            if (filters.color) params.set('filters[color][$eq]', filters.color);
            if (filters.condition) params.set('filters[condition][$eq]', filters.condition);
            if (filters.material) params.set('filters[material][$eq]', filters.material);
            if (filters.brand) params.set('filters[brand][$containsi]', filters.brand);
            if (filters.designerSchool) params.set('filters[designerSchool][$containsi]', filters.designerSchool);
        } else {
            if (filters.productType) params.set('productType', filters.productType);
            if (filters.forSell === true) params.set('forSell', 'true');
            if (filters.forRent === true) params.set('forRent', 'true');
            if (filters.isVirtual === true) params.set('isVirtual', 'true');
            else if (filters.isVirtual === false) params.set('isVirtual', 'false');
            if (filters.openToShipping === true) params.set('openToShipping', 'true');
            if (filters.localPickup?.enabled) {
                params.set('openToPickUp', 'true');
                if (location && filters.localPickup.distance) {
                    params.set('lat', location.latitude);
                    params.set('lng', location.longitude);
                    params.set('radius', filters.localPickup.distance);
                }
            }
            if (filters.category) params.set('category', filters.category);
            if (filters.size) params.set('size', filters.size);
            if (filters.color) params.set('color', filters.color);
            if (filters.condition) params.set('condition', filters.condition);
            if (filters.material) params.set('material', filters.material);
            if (filters.brand) params.set('brand_contains', filters.brand);
            if (filters.designerSchool) params.set('designerSchool_contains', filters.designerSchool);
        }
    }

    return params.toString();
};

/**
 * Fetch product count from Strapi to support pagination.
 * Uses the same filters but hits /products/count endpoint.
 */
const fetchProductCount = async ({ searchTerm, filters, sortBy, location }, state, token) => {
    const params = new URLSearchParams();
    const pathname = window.location.pathname;
    const isV5 = BACKEND_URL.includes('1338') || process.env.REACT_APP_IS_STRAPI_V5 === 'true';

    // Apply same base filters as the main query (minus pagination/sort)
    if (pathname.includes('/merchant/')) {
        const merchantId = pathname.split('/merchant/')[1];
        if (isV5) {
            params.set('filters[users_permissions_user][id][$eq]', merchantId);
        } else {
            params.set('users_permissions_user.id', merchantId);
        }
        params.set('activeListing', 'true');
        params.set('stock_ne', '0');
    } else if (pathname.includes('/listing') || pathname.includes('/dashboard/listings')) {
        if (state?.auth?.user?.id) {
            if (isV5) {
                params.set('filters[users_permissions_user][id][$eq]', state.auth.user.id);
            } else {
                params.set('users_permissions_user', state.auth.user.id);
            }
        }
    } else if (pathname.includes('/favorites')) {
        if (state?.auth?.user?.id) {
            if (isV5) {
                params.set('filters[being_liked_by][id][$eq]', state.auth.user.id);
            } else {
                params.set('being_liked_by.id', state.auth.user.id);
            }
            params.set('activeListing', 'true');
            params.set('stock_ne', '0');
        }
    } else {
        params.set('activeListing', 'true');
        params.set('stock_ne', '0');
    }

    if (searchTerm && searchTerm.trim()) {
        if (isV5) {
            params.set('filters[name][$containsi]', searchTerm.trim());
        } else {
            params.set('_q', searchTerm.trim());
        }
    }

    if (filters) {
        if (filters.productType) params.set('productType', filters.productType);
        if (filters.forSell === true) params.set('forSell', 'true');
        if (filters.forRent === true) params.set('forRent', 'true');
        if (filters.isVirtual === true) params.set('isVirtual', 'true');
        else if (filters.isVirtual === false) params.set('isVirtual', 'false');
        if (filters.openToShipping === true) params.set('openToShipping', 'true');
        if (filters.localPickup?.enabled) {
            params.set('openToPickUp', 'true');
            if (location && filters.localPickup.distance) {
                params.set('lat', location.latitude);
                params.set('lng', location.longitude);
                params.set('radius', filters.localPickup.distance);
            }
        }
        if (filters.category) params.set('category', filters.category);
        if (filters.size) params.set('size', filters.size);
        if (filters.color) params.set('color', filters.color);
        if (filters.condition) params.set('condition', filters.condition);
        if (filters.material) params.set('material', filters.material);
        if (filters.brand) params.set('brand_contains', filters.brand);
        if (filters.designerSchool) params.set('designerSchool_contains', filters.designerSchool);
    }

    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const urlSuffix = isV5 && !BACKEND_URL.endsWith('/api') ? '/api/products/count' : '/products/count';
    const response = await fetch(`${BACKEND_URL}${urlSuffix}?${params.toString()}`, { headers });

    if (!response.ok) {
        throw new Error(`Count request failed: ${response.status}`);
    }

    return response.json();
};

/**
 * Main search function — drop-in replacement for the Algolia searchProducts.
 * Same interface: ({ searchTerm, filters, sortBy, offset, location }, getState) => result
 */
export const searchProducts = async ({ searchTerm, filters, sortBy, offset = 0, location }, getState) => {
    try {
        const state = getState();
        const token = state?.auth?.token || localStorage.getItem('token');
        const queryString = buildStrapiQuery({ searchTerm, filters, sortBy, offset, location }, state);

        const headers = { 'Content-Type': 'application/json' };
        if (token) headers['Authorization'] = `Bearer ${token}`;

        const isV5 = BACKEND_URL.includes('1338') || process.env.REACT_APP_IS_STRAPI_V5 === 'true';
        const urlSuffix = isV5 && !BACKEND_URL.endsWith('/api') ? '/api/products' : '/products';
        const fetchUrl = `${BACKEND_URL}${urlSuffix}?${queryString}`;

        // Fetch products and count. Custom v5 backend supports /count endpoint.
        const [productsResponse, countResponse] = await Promise.all([
            fetch(fetchUrl, { headers }),
            fetchProductCount({ searchTerm, filters, sortBy, location }, state, token)
        ]);

        if (!productsResponse.ok) {
            throw new Error(`Products request failed: ${productsResponse.status}`);
        }

        const jsonResponse = await productsResponse.json();
        
        let products = [];
        let totalCount = 0;

        if (isV5) {
            products = Array.isArray(jsonResponse) ? jsonResponse : (jsonResponse.data || []);
            totalCount = jsonResponse.meta?.pagination?.total || products.length;
        } else {
            products = jsonResponse;
            totalCount = countResponse || products.length;
        }

        // Map to same format as Algolia results for compatibility
        const mappedProducts = products.map(product => {
            // Handle v5 document structure if needed (though typical transforms might flatten it earlier, fetch here is raw)
            const item = product.attributes ? { id: product.id, ...product.attributes } : product;
            return {
                ...item,
                id: item.documentId || item.id, // Prefer documentId for v5
                likes_count: item.being_liked_by ? item.being_liked_by.length : 0
            };
        });

        return {
            products: mappedProducts,
            totalResults: totalCount,
            currentPage: Math.floor(offset / HITS_PER_PAGE),
            totalPages: Math.ceil(totalCount / HITS_PER_PAGE)
        };
    } catch (error) {
        console.error('Strapi search error:', error);
        throw error;
    }
};

/**
 * Search suggestions for autocomplete — lightweight name-only search.
 */
export const searchSuggestions = async (query, token) => {
    if (!query || !query.trim()) return [];

    try {
        const headers = { 'Content-Type': 'application/json' };
        if (token) headers['Authorization'] = `Bearer ${token}`;

        const isV5 = BACKEND_URL.includes('1338') || process.env.REACT_APP_IS_STRAPI_V5 === 'true';
        const params = new URLSearchParams();
        if (isV5) {
            params.set('filters[name][$containsi]', query.trim());
            params.set('filters[activeListing][$eq]', 'true');
            params.set('filters[stock][$ne]', '0');
        } else {
            params.set('_q', query.trim());
            params.set('activeListing', 'true');
            params.set('stock_ne', '0');
        }
        params.set('_limit', '5');

        const urlSuffix = isV5 && !BACKEND_URL.endsWith('/api') ? '/api/products' : '/products';
        const response = await fetch(`${BACKEND_URL}${urlSuffix}?${params.toString()}`, { headers });

        if (!response.ok) return [];

        const jsonResponse = await response.json();
        const products = isV5 ? (jsonResponse.data || []) : jsonResponse;

        // Return unique names
        const uniqueNames = [...new Set(products.map(p => {
            const item = p.attributes || p;
            return item.name;
        }).filter(Boolean))];
        return uniqueNames.map(name => ({ objectID: name, query: name }));
    } catch (error) {
        return [];
    }
};
